import React from 'react';
import { Badge, Button, Form, InputGroup, Spinner, Table } from 'react-bootstrap';
import { toISODate } from '../utils/thaiDate'

// ตัวเลือกและตัวช่วยที่ใช้ร่วมกันระหว่างใบอนุญาตกลับบ้าน (DisciplineHomeLeave)
// และใบอนุญาตออกนอกสถานที่ (DisciplineOuting)

export const OTHER_OPTION = 'อื่นๆ';

// เหตุผลกลับบ้าน — ค้างคืนที่บ้าน
export const REASON_OPTIONS = [
  'ป่วย',
  'หมอนัด',
  'งานแต่ง',
  'ครอบครัวเสียชีวิต',
  'งานบุญ/งานประเพณี',
  'ธุระครอบครัว',
  'กลับบ้านประจำเดือน',
  'ผู้ปกครองมารับ',
  OTHER_OPTION
];

// เหตุผลออกนอกสถานที่ — ไป-กลับภายในวันเดียวกัน
export const OUTING_REASON_OPTIONS = [
  'หมอนัด/พบแพทย์',
  'ตัดผม',
  'ซื้ออุปกรณ์การเรียน',
  'ติดต่อราชการ',
  'แข่งขัน/กิจกรรมนอกโรงเรียน',
  'ธุระครอบครัว',
  'ผู้ปกครองมารับ',
  OTHER_OPTION
];

export const GUARDIAN_RELATIONS = ['พ่อ', 'แม่', 'พี่', 'น้อง', 'ลุง', 'ป้า', 'น้า', 'อา', 'ปู่/ย่า', 'ตา/ยาย', OTHER_OPTION];

// ─── วันที่/เวลา ──────────────────────────────────────────────────────────────
// ใช้เวลาท้องถิ่นเสมอ ไม่ใช้ toISOString ตรงๆ เพราะ UTC+7 จะได้วันที่ย้อนหลังก่อน 07:00 น.
const isoOf = (date) => toISODate(date);

export const todayISO = () => isoOf(new Date());

export const addDaysISO = (days, baseISO) => {
  const base = baseISO ? new Date(`${baseISO}T00:00:00`) : new Date();
  base.setDate(base.getDate() + days);
  return isoOf(base);
};

export const thaiDate = (value) => (
  value ? new Date(value).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: '2-digit' }) : ''
);

export const thaiTime = (value) => (
  value ? new Date(value).toLocaleTimeString('th-TH', { hour: '2-digit', minute: '2-digit' }) : ''
);

// จำนวนวันแบบนับรวมวันแรกและวันสุดท้าย — ใช้บอกผู้ใช้ว่าใบนี้กี่วัน
export const dayCount = (fromISO, toISO) => {
  if (!fromISO || !toISO) return 0;
  const diff = new Date(`${toISO}T00:00:00`) - new Date(`${fromISO}T00:00:00`);
  return diff < 0 ? 0 : Math.round(diff / 86400000) + 1;
};

// ช่วงเวลาในวันเดียวกัน คืนค่าเป็นข้อความ เช่น "7 ชม. 30 นาที"
export const durationText = (fromTime, toTime) => {
  if (!fromTime || !toTime) return '';
  const [fh, fm] = fromTime.split(':').map(Number);
  const [th, tm] = toTime.split(':').map(Number);
  const minutes = (th * 60 + tm) - (fh * 60 + fm);
  if (minutes <= 0) return '';
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return [hours ? `${hours} ชม.` : '', rest ? `${rest} นาที` : ''].filter(Boolean).join(' ');
};

// ป้ายกำกับการกระทำ ใช้ทั้งในกล่องยืนยันและข้อความผลลัพธ์
export const STATUS_ACTION_LABEL = { approved: 'อนุมัติ', rejected: 'ปฏิเสธ', cancelled: 'ยกเลิก' };

// ─── ปุ่มตัวเลือก ─────────────────────────────────────────────────────────────
export const OptionButtons = ({ options, value, onSelect, invalid }) => (
  <div className={`d-flex flex-wrap gap-2${invalid ? ' border border-danger rounded p-2' : ''}`}>
    {options.map(option => (
      <Button
        key={option}
        type="button"
        size="sm"
        variant={value === option ? 'primary' : 'outline-secondary'}
        onClick={() => onSelect(value === option ? '' : option)}
      >
        {option}
      </Button>
    ))}
  </div>
);

// แบบเลือกได้หลายค่า เช่น พ่อกับแม่มารับพร้อมกัน — ติ๊กแล้วมีเครื่องหมายถูกกำกับ
export const MultiOptionButtons = ({ options, values = [], onToggle }) => (
  <div className="d-flex flex-wrap gap-2">
    {options.map(option => {
      const active = values.includes(option);
      return (
        <Button
          key={option}
          type="button"
          size="sm"
          variant={active ? 'primary' : 'outline-secondary'}
          onClick={() => onToggle(option)}
        >
          {active && <i className="ri-check-line me-1"></i>}{option}
        </Button>
      );
    })}
  </div>
);

// สถานะการอนุญาต — ใช้ปุ่มเขียว/แดง ให้แยกออกจากกันได้ทันทีตั้งแต่ตอนกรอกฟอร์ม
const PERMISSION_OPTIONS = [
  { value: 'approved', label: 'อนุญาต', variant: 'success', icon: 'ri-check-line' },
  { value: 'rejected', label: 'ไม่อนุญาต', variant: 'danger', icon: 'ri-close-line' }
];

export const PermissionButtons = ({ value, onSelect }) => (
  <div className="d-flex gap-2">
    {PERMISSION_OPTIONS.map(option => (
      <Button
        key={option.value}
        type="button"
        className="flex-fill"
        variant={value === option.value ? option.variant : `outline-${option.variant}`}
        onClick={() => onSelect(option.value)}
      >
        <i className={`${option.icon} me-1`}></i>{option.label}
      </Button>
    ))}
  </div>
);

// ปุ่มลัดวันที่ — งานส่วนใหญ่คือวันนี้หรือพรุ่งนี้ ไม่ต้องเปิดปฏิทิน
const DEFAULT_DATE_CHIPS = [{ label: 'วันนี้', days: 0 }, { label: 'พรุ่งนี้', days: 1 }, { label: 'มะรืนนี้', days: 2 }];

export const QuickDateChips = ({ value, onSelect, options = DEFAULT_DATE_CHIPS }) => (
  <div className="d-flex flex-wrap gap-1 mt-1">
    {options.map(option => {
      const iso = addDaysISO(option.days);
      return (
        <Button
          key={option.label}
          type="button"
          size="sm"
          variant={value === iso ? 'primary' : 'outline-secondary'}
          onClick={() => onSelect(iso)}
        >
          {option.label}
        </Button>
      );
    })}
  </div>
);

export const FieldLabel = ({ children, required, hint }) => (
  <Form.Label className="fw-semibold mb-1">
    {children}
    {required && <span className="text-danger ms-1">*</span>}
    {hint && <span className="fs-xs fw-normal text-secondary ms-1">({hint})</span>}
  </Form.Label>
);

// ชั้นศาสนา (is_class/is_room ของปีเดียวกัน) — นักเรียนที่ยังไม่ลงทะเบียนศาสนาจะไม่มีค่า
export const islamicClassText = (student) => (
  student?.islamic_class
    ? `ศาสนา ${student.islamic_class}${student.islamic_room_no ? `/${student.islamic_room_no}` : ''}`
    : ''
);

// ชั้นสามัญของปีนั้น — คนที่จบ ม.6 แล้วเรียนศาสนาอย่างเดียวไม่มีค่า (c_id เป็น null)
export const generalClassText = (student) => (
  student?.c_id ? `ม.${student.c_id}/${student.r_no ?? ''}` : ''
);

// ชั้นสามัญ · ชั้นศาสนา เท่าที่มี — ใช้แทนการต่อ ม.{c_id}/{r_no} ตรงๆ
export const studentClassText = (student) => (
  [generalClassText(student), islamicClassText(student)].filter(Boolean).join(' · ')
);

// ชื่ออาหรับ — dir=rtl ให้เรียงอักษร/เครื่องหมายถูกต้อง แต่บังคับชิดซ้ายให้ตรงกับชื่อไทยด้านบน
const ArabicName = ({ name }) => (
  name ? <div className="fs-xs" dir="rtl" lang="ar" style={{ textAlign: 'left' }}>{name}</div> : null
);

// ชื่อไทย + ชื่ออาหรับ + ชั้นสามัญ/ชั้นศาสนา ใช้ทั้งรายการค้นหาและตารางคำขอ
export const StudentNameCell = ({ student }) => (
  <>
    <div className="fw-semibold">{student.st_name}</div>
    <ArabicName name={student.st_arabicname} />
    <div className="fs-xs text-secondary">
      {student.st_idnumber}
      {studentClassText(student) && ` · ${studentClassText(student)}`}
    </div>
  </>
);

export const statusBadge = (status) => {
  const map = {
    pending: ['warning', 'รออนุมัติ'],
    approved: ['success', 'อนุมัติแล้ว'],
    rejected: ['danger', 'ปฏิเสธ'],
    cancelled: ['secondary', 'ยกเลิก']
  };
  const [variant, label] = map[status] || ['secondary', status];
  return <Badge bg={variant}>{label}</Badge>;
};

// ตัวกรองสถานะแบบปุ่ม — กดครั้งเดียวเห็นผล ใช้ง่ายกว่า dropdown บนมือถือ
export const STATUS_FILTERS = [
  { value: 'all', label: 'ทุกสถานะ', variant: 'dark' },
  { value: 'pending', label: 'รออนุมัติ', variant: 'warning' },
  { value: 'approved', label: 'อนุมัติแล้ว', variant: 'success' },
  { value: 'rejected', label: 'ปฏิเสธ', variant: 'danger' },
  { value: 'cancelled', label: 'ยกเลิก', variant: 'secondary' }
];

export const StatusFilterChips = ({ value, onSelect }) => (
  <div className="d-flex flex-wrap gap-1">
    {STATUS_FILTERS.map(item => (
      <Button
        key={item.value}
        size="sm"
        variant={value === item.value ? item.variant : `outline-${item.variant}`}
        onClick={() => onSelect(item.value)}
      >
        {item.label}
      </Button>
    ))}
  </div>
);

const UPLOAD_BASE = (process.env.REACT_APP_API || 'http://localhost:3002/api').replace(/\/api\/?$/, '').replace(/\/$/, '');

export const studentPhotoUrl = (photo) => (
  photo ? `${UPLOAD_BASE}${photo.startsWith('/') ? photo : `/${photo}`}` : null
);

const StudentPhoto = ({ student, size = 64 }) => (
  studentPhotoUrl(student.st_photo) ? (
    <img
      src={studentPhotoUrl(student.st_photo)}
      alt={`รูปภาพ ${student.st_name}`}
      width={size}
      height={size}
      className="rounded-circle border bg-white flex-shrink-0"
      style={{ objectFit: 'cover' }}
    />
  ) : (
    <div
      className="rounded-circle bg-secondary text-white d-flex align-items-center justify-content-center flex-shrink-0"
      style={{ width: size, height: size, fontSize: size / 2.6 }}
      aria-label={`ไม่มีรูปภาพ ${student.st_name}`}
    >
      {student.st_name?.trim().charAt(0) || '?'}
    </div>
  )
);

// การ์ดนักเรียนที่เลือกไว้ ใช้เหนือฟอร์มบันทึกคำขอ — onClear ให้กดเปลี่ยนนักเรียนได้ทันที
export const SelectedStudentCard = ({ student, onClear }) => (
  <div className="alert alert-primary py-2 d-flex align-items-center gap-3 mb-3">
    <StudentPhoto student={student} />
    <div className="flex-grow-1">
      <div className="fw-semibold">{student.st_name}</div>
      <ArabicName name={student.st_arabicname} />
      <span className="fs-xs">
        {studentClassText(student) && `${studentClassText(student)} · `}{student.st_idnumber}
      </span>
    </div>
    {onClear && (
      <Button variant="outline-primary" size="sm" className="flex-shrink-0" onClick={onClear}>
        <i className="ri-repeat-line me-1"></i>เปลี่ยน
      </Button>
    )}
  </div>
);

// ─── ขั้นที่ 1: ค้นหาและเลือกนักเรียน ─────────────────────────────────────────
// ค้นเมื่อกดปุ่ม/กด Enter เท่านั้น — ไม่ดึงรายชื่อทั้งโรงเรียนตอนเปิดหน้า
// เลือกแล้วรายการค้นหาจะยุบไป เหลือแค่การ์ดนักเรียน เพื่อไม่ให้หน้ายาวเกินจำเป็น
export const StudentPicker = ({ keyword, onKeywordChange, onSubmit, students, loading, searchedTerm, selected, onSelect, onClear }) => {
  if (selected) return <SelectedStudentCard student={selected} onClear={onClear} />;
  return (
    <>
      <Form onSubmit={onSubmit}>
        <InputGroup>
          <Form.Control
            value={keyword}
            onChange={e => onKeywordChange(e.target.value)}
            placeholder="เลขประจำตัว หรือชื่อนักเรียน"
            inputMode="search"
            autoComplete="off"
          />
          {keyword && (
            <Button variant="outline-secondary" type="button" onClick={() => onKeywordChange('')} aria-label="ล้างคำค้นหา">
              <i className="ri-close-line"></i>
            </Button>
          )}
          <Button type="submit" disabled={loading}>
            {loading
              ? <Spinner size="sm" animation="border" />
              : <><i className="ri-search-line me-1"></i>ค้นหา</>}
          </Button>
        </InputGroup>
      </Form>

      {students.length > 0 ? (
        <>
          <div className="fs-xs text-secondary mt-2">
            ผลค้นหา “{searchedTerm}” · {students.length} คน — กดเลือกนักเรียน
          </div>
          <div style={{ maxHeight: 300, overflowY: 'auto' }}>
            <Table hover size="sm" className="mb-0">
              <tbody>
                {students.map(student => (
                  <tr key={student.st_id} role="button" onClick={() => onSelect(student)}>
                    <td>
                      <StudentNameCell student={student} />
                    </td>
                    <td className="text-end align-middle text-secondary" style={{ width: 28 }}>
                      <i className="ri-arrow-right-s-line"></i>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </>
      ) : (
        <div className="text-center text-secondary py-4 fs-sm">
          {searchedTerm
            ? `ไม่พบนักเรียนที่ตรงกับ “${searchedTerm}”`
            : 'ป้อนเลขประจำตัวหรือชื่อนักเรียน แล้วกดปุ่มค้นหา'}
        </div>
      )}
    </>
  );
};

// ─── ปุ่มจัดการคำขอ ───────────────────────────────────────────────────────────
// รออนุมัติ → อนุมัติ/ปฏิเสธ · อนุมัติแล้ว → ยกเลิกได้ · ปฏิเสธ/ยกเลิกแล้ว → จบแล้ว ไม่มีปุ่ม
export const RequestActions = ({ req, onUpdate, busy, stacked }) => {
  if (req.status === 'pending') {
    return (
      <div className={stacked ? 'd-grid gap-2' : 'd-flex gap-1 justify-content-center'}>
        <Button size="sm" variant="success" disabled={busy} onClick={() => onUpdate(req, 'approved')}>
          <i className="ri-check-line me-1"></i>อนุมัติ
        </Button>
        <Button size="sm" variant="outline-danger" disabled={busy} onClick={() => onUpdate(req, 'rejected')}>
          <i className="ri-close-line me-1"></i>ปฏิเสธ
        </Button>
      </div>
    );
  }
  if (req.status === 'approved') {
    return (
      <div className={stacked ? 'd-grid' : ''}>
        <Button size="sm" variant="outline-secondary" disabled={busy} onClick={() => onUpdate(req, 'cancelled')}>
          ยกเลิกใบนี้
        </Button>
      </div>
    );
  }
  return <span className="fs-xs text-secondary">—</span>;
};

// ─── รายการคำขอ — ตารางบนจอใหญ่ / การ์ดบนมือถือ ───────────────────────────────
// columns: [{ header, cell(req) }] ให้แต่ละหน้ากำหนดคอลัมน์ของตัวเองได้
export const RequestList = ({ rows, loading, keyOf, columns, statusCell, onUpdate, busyKey, emptyText }) => {
  if (loading) {
    return (
      <div className="text-center text-secondary py-5">
        <Spinner animation="border" size="sm" className="me-2" />กำลังโหลด...
      </div>
    );
  }
  if (rows.length === 0) {
    return <div className="text-center text-secondary py-5">{emptyText}</div>;
  }
  return (
    <>
      <div className="d-none d-lg-block">
        <Table responsive hover className="mb-0 align-middle">
          <thead className="table-secondary">
            <tr>
              <th>นักเรียน</th>
              {columns.map(col => <th key={col.header}>{col.header}</th>)}
              <th className="text-center">สถานะ</th>
              <th className="text-center" style={{ width: 180 }}>จัดการ</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(req => (
              <tr key={keyOf(req)}>
                <td><StudentNameCell student={req} /></td>
                {columns.map(col => <td key={col.header}>{col.cell(req)}</td>)}
                <td className="text-center">{statusCell(req)}</td>
                <td className="text-center">
                  <RequestActions req={req} onUpdate={onUpdate} busy={busyKey === keyOf(req)} />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <div className="d-lg-none d-grid gap-2 p-2">
        {rows.map(req => (
          <div key={keyOf(req)} className={`border rounded p-2 ${req.status === 'pending' ? 'border-warning' : ''}`}>
            <div className="d-flex justify-content-between align-items-start gap-2">
              <StudentNameCell student={req} />
              <div className="text-end flex-shrink-0">{statusCell(req)}</div>
            </div>
            {columns.map(col => (
              <div key={col.header} className="mt-2">
                <div className="fs-xs text-secondary">{col.header}</div>
                {col.cell(req)}
              </div>
            ))}
            <div className="mt-3">
              <RequestActions req={req} onUpdate={onUpdate} busy={busyKey === keyOf(req)} stacked />
            </div>
          </div>
        ))}
      </div>
    </>
  );
};
